import { z } from "zod";
import { fcfa, MOIS, useApiEnveloppe } from "../../api/hooks";
import { EtatChargement, EtatErreur, ListeAvis } from "../../composants/Avis";
import { Tableau, Tuiles } from "../../composants/Tableau";
import type { EcranProps } from "../types";

// ===========================================================================
// PAIE
// ===========================================================================

const PaieSchema = z.object({
  annee: z.number(),
  mois: z.number(),
  calculeeLe: z.string(),
  calculeePar: z.string().nullable(),
  salaries: z.array(
    z.object({
      nom: z.string(),
      brut: z.number(),
      transportExonere: z.number(),
      baseIts: z.number(),
      itsBareme: z.number(),
      ricf: z.number(),
      its: z.number(),
      cnpsSalariale: z.number(),
      net: z.number(),
      totalCnpsPatronale: z.number(),
      aDeclarerDgi: z.number(),
      aDeclarerCnps: z.number(),
    }),
  ),
  totaux: z.object({
    brut: z.number(),
    its: z.number(),
    cnpsSalariale: z.number(),
    net: z.number(),
    cnpsPatronale: z.number(),
    aDeclarerDgi: z.number(),
    aDeclarerCnps: z.number(),
  }),
  declarations: z.array(z.object({ nature: z.string(), montant: z.number() })),
});

export function Paie({ dossierId, annee, mois }: EcranProps) {
  const q = useApiEnveloppe(
    ["paie", dossierId, annee, mois],
    `/dossiers/${dossierId}/social/paie/${annee}/${mois}`,
    PaieSchema,
  );

  if (q.isLoading) return <EtatChargement quoi="de la paie" />;
  if (q.error) return <EtatErreur erreur={q.error} />;

  if (!q.data?.valeur)
    return (
      <div>
        <p className="a-faire">
          Aucune paie enregistrée pour {MOIS[mois - 1]} {annee}.
        </p>
        <p className="resume">
          Le bouton « Depuis le personnel » reprend les salariés actifs{" "}
          <strong>en conservant les montants déjà saisis</strong>.
        </p>
        <ListeAvis avis={q.data?.avis ?? []} />
      </div>
    );

  const p = q.data.valeur;

  return (
    <div>
      <p className="resume">
        <strong>net = brut − ITS − CNPS salariale.</strong> La prime de transport
        est retirée de la base ITS parce qu'elle est exonérée, mais elle{" "}
        <strong>ne se rajoute pas au net</strong> : elle est déjà dans le brut.
      </p>

      <Tuiles
        tuiles={[
          { titre: "Salariés", valeur: p.salaries.length },
          { titre: "Masse brute", valeur: fcfa(p.totaux.brut) },
          { titre: "ITS", valeur: fcfa(p.totaux.its) },
          { titre: "CNPS salariale", valeur: fcfa(p.totaux.cnpsSalariale) },
          { titre: "Net à payer", valeur: fcfa(p.totaux.net) },
          { titre: "CNPS patronale", valeur: fcfa(p.totaux.cnpsPatronale) },
        ]}
      />

      <section>
        <h2>Détail par salarié</h2>
        <Tableau
          colonnes={[
            { cle: "n", titre: "Salarié", rendu: (s) => s.nom },
            { cle: "b", titre: "Brut", montant: true, rendu: (s) => fcfa(s.brut) },
            {
              cle: "t",
              titre: "Transport exonéré",
              montant: true,
              rendu: (s) => fcfa(s.transportExonere),
            },
            { cle: "bi", titre: "Base ITS", montant: true, rendu: (s) => fcfa(s.baseIts) },
            { cle: "bar", titre: "Barème", montant: true, rendu: (s) => fcfa(s.itsBareme) },
            { cle: "ricf", titre: "RICF", montant: true, rendu: (s) => fcfa(s.ricf) },
            { cle: "its", titre: "ITS", montant: true, rendu: (s) => fcfa(s.its) },
            { cle: "cs", titre: "CNPS sal.", montant: true, rendu: (s) => fcfa(s.cnpsSalariale) },
            { cle: "net", titre: "Net", montant: true, rendu: (s) => fcfa(s.net) },
          ]}
          lignes={p.salaries}
          cle={(s) => s.nom}
        />
      </section>

      <section>
        <h2>Déclarations produites</h2>
        <p className="resume">
          Si la paie existe, elle <strong>prime</strong> sur l'estimation par les
          comptes 447 et 661.
        </p>
        <Tableau
          colonnes={[
            { cle: "n", titre: "Déclaration", rendu: (d) => d.nature },
            { cle: "m", titre: "Montant", montant: true, rendu: (d) => fcfa(d.montant) },
          ]}
          lignes={p.declarations}
          cle={(d) => d.nature}
        />
      </section>

      <ListeAvis avis={q.data.avis} />
    </div>
  );
}

// ===========================================================================
// PERSONNEL
// ===========================================================================

const PersonnelSchema = z.object({
  membres: z.array(
    z.object({
      id: z.string(),
      nom: z.string(),
      poste: z.string().nullable(),
      contrat: z.string(),
      contratEcrit: z.boolean(),
      dateEmbauche: z.string().nullable(),
      declareCnps: z.boolean(),
      numeroCnps: z.string().nullable(),
      visiteMedicale: z.boolean(),
      dateVisiteMedicale: z.string().nullable(),
      dateFin: z.string().nullable(),
      dateSortie: z.string().nullable(),
      note: z.string().nullable(),
    }),
  ),
  effectif: z.number(),
  declares: z.number(),
  nonDeclares: z.number(),
  visitesAJour: z.number(),
  sansContratEcrit: z.number(),
  echeancesATraiter: z.number(),
  sortis: z.number(),
  alertes: z.array(
    z.object({
      type: z.string(),
      salarie: z.string(),
      message: z.string(),
      joursRestants: z.number().nullable(),
    }),
  ),
});

export function Personnel({ dossierId }: EcranProps) {
  const q = useApiEnveloppe(
    ["personnel", dossierId],
    `/dossiers/${dossierId}/social/personnel`,
    PersonnelSchema,
  );

  if (q.isLoading) return <EtatChargement quoi="de l'inventaire du personnel" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data?.valeur)
    return (
      <div>
        <p className="a-faire">Aucun salarié inscrit à l'inventaire.</p>
        <ListeAvis avis={q.data?.avis ?? []} />
      </div>
    );

  const p = q.data.valeur;

  return (
    <div>
      <Tuiles
        tuiles={[
          { titre: "Effectif", valeur: p.effectif },
          { titre: "Déclarés CNPS", valeur: p.declares },
          { titre: "Non déclarés", valeur: p.nonDeclares, alerte: p.nonDeclares > 0 },
          { titre: "Visites à jour", valeur: p.visitesAJour },
          {
            titre: "Sans contrat écrit",
            valeur: p.sansContratEcrit,
            alerte: p.sansContratEcrit > 0,
          },
          {
            titre: "Échéances à traiter",
            valeur: p.echeancesATraiter,
            alerte: p.echeancesATraiter > 0,
          },
          { titre: "Sortis", valeur: p.sortis },
        ]}
      />

      {p.alertes.length > 0 && (
        <section>
          <h2>Alertes automatiques</h2>
          <ul className="alertes">
            {p.alertes.map((a, i) => (
              <li key={i} className={`alerte-personnel alerte-personnel--${a.type}`}>
                {a.message}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2>Inventaire</h2>
        <Tableau
          colonnes={[
            { cle: "n", titre: "Nom", rendu: (m) => m.nom },
            { cle: "p", titre: "Poste", rendu: (m) => m.poste ?? "—" },
            { cle: "c", titre: "Contrat", rendu: (m) => m.contrat },
            {
              cle: "ce",
              titre: "Écrit",
              rendu: (m) => (
                <span className={m.contratEcrit ? "oui" : "non"}>
                  {m.contratEcrit ? "oui" : "non"}
                </span>
              ),
            },
            { cle: "emb", titre: "Embauche", rendu: (m) => m.dateEmbauche ?? "—" },
            {
              cle: "cnps",
              titre: "CNPS",
              rendu: (m) => (
                <span className={m.declareCnps ? "oui" : "non"}>
                  {m.declareCnps ? (m.numeroCnps ?? "déclaré") : "non déclaré"}
                </span>
              ),
            },
            {
              cle: "vm",
              titre: "Visite médicale",
              rendu: (m) => (
                <span className={m.visiteMedicale ? "oui" : "non"}>
                  {m.visiteMedicale ? (m.dateVisiteMedicale ?? "oui") : "non"}
                </span>
              ),
            },
            { cle: "fin", titre: "Échéance", rendu: (m) => m.dateFin ?? "—" },
            { cle: "sor", titre: "Sortie", rendu: (m) => m.dateSortie ?? "—" },
          ]}
          lignes={p.membres}
          cle={(m) => m.id}
        />
      </section>

      <ListeAvis avis={q.data.avis} />
    </div>
  );
}
